<template>
  <article class="card documents-legal-file-card">
    <div class="card-body documents-legal-file-card__body">
      <header class="documents-legal-file-card__header">
        <h4>{{ text.legalFileCard.title }}</h4>
        <span v-if="showStatusBadge" class="documents-legal-file-card__status-badge" :class="`is-${statusVariant}`">
          {{ statusText }}
        </span>
      </header>

      <p v-if="records.length === 0" class="documents-legal-file-card__empty">
        {{ text.legalFileCard.empty }}
      </p>

      <ul v-else class="documents-legal-file-card__list">
        <li v-for="item in records" :key="item.key" class="documents-legal-file-card__item">
          <div class="documents-legal-file-card__item-main">
            <p class="documents-legal-file-card__item-name">{{ item.fileName }}</p>
            <p v-if="item.hasDocument" class="documents-legal-file-card__item-dates">
              {{ resolveValidityLabel(item) }}
            </p>
            <button
              v-else
              type="button"
              class="documents-legal-file-card__assign-btn"
              :title="text.legalFileCard.actionTitles.assign"
              @click="$emit('edit-item', item)"
            >
              {{ text.legalFileCard.buttons.assign }}
            </button>
            <p class="documents-legal-file-card__item-comments" :title="item.displayComments || item.comments || ''">
              {{ item.displayComments || item.comments || '' }}
            </p>
          </div>

          <div class="documents-legal-file-card__actions">
            <button
              v-if="item.hasDocument"
              type="button"
              class="app-table-icon-btn app-table-icon-btn--view"
              :title="text.legalFileCard.actionTitles.download"
              @click="$emit('download-item', item)"
            >
              <i class="fa fa-download" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              class="app-table-icon-btn app-table-icon-btn--edit"
              :title="item.hasDocument ? text.legalFileCard.actionTitles.edit : text.legalFileCard.actionTitles.add"
              @click="$emit('edit-item', item)"
            >
              <i class="fa fa-pencil" aria-hidden="true"></i>
            </button>

            <button
              v-if="item.hasUploadedFile"
              type="button"
              class="app-table-icon-btn app-table-icon-btn--delete"
              :title="text.legalFileCard.actionTitles.delete"
              @click="$emit('delete-item', item)"
            >
              <i class="fa fa-trash" aria-hidden="true"></i>
            </button>
          </div>
        </li>
      </ul>

      <button
        v-if="showReportButton"
        type="button"
        class="btn btn-light documents-legal-file-card__report-btn"
        @click="$emit('download-report')"
      >
        <i class="fa fa-download" aria-hidden="true"></i>
        {{ text.legalFileCard.reportButton }}
      </button>
    </div>
  </article>
</template>

<script>
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('documents');

export default {
  name: 'DocumentsLegalFileCard',
  emits: ['download-item', 'edit-item', 'delete-item', 'download-report'],
  props: {
    records: {
      type: Array,
      default: () => [],
    },
    statusText: {
      type: String,
      default: 'INCOMPLETO',
    },
    statusVariant: {
      type: String,
      default: 'incomplete',
    },
    showStatusBadge: {
      type: Boolean,
      default: true,
    },
    showReportButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text,
    };
  },
  methods: {
    normalizeDateValue(value) {
      const normalized = String(value || '').trim();
      return normalized === this.text.common.emptyValue ? '' : normalized;
    },
    resolveValidityLabel(item) {
      const start = this.normalizeDateValue(item?.validityStart);
      const end = this.normalizeDateValue(item?.validityEnd);

      if (start === '' && end === '') {
        return this.text.common.noValidity;
      }

      if (start !== '' && end !== '') {
        return `${start} - ${end}`;
      }

      return start || end;
    },
  },
};
</script>

<style scoped>
.documents-legal-file-card,
.documents-legal-file-card .card-body {
  background: #0d61a4;
  border-radius: 16px;
  border: 0;
}

.documents-legal-file-card__body {
  padding: 14px;
}

.documents-legal-file-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.documents-legal-file-card__header h4 {
  margin: 0;
  color: #f8fafc;
  font-size: 17px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.documents-legal-file-card__status-badge {
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid transparent;
}

.documents-legal-file-card__status-badge.is-complete {
  color: #dcfce7;
  background: rgba(34, 197, 94, 0.22);
  border-color: rgba(134, 239, 172, 0.35);
}

.documents-legal-file-card__status-badge.is-incomplete {
  color: #fef3c7;
  background: rgba(245, 158, 11, 0.22);
  border-color: rgba(253, 224, 71, 0.35);
}

.documents-legal-file-card__list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.documents-legal-file-card__empty {
  margin: 0;
  padding: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.35);
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.4;
  color: #e2e8f0;
}

.documents-legal-file-card__item {
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  padding: 10px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.08);
}

.documents-legal-file-card__item-main {
  min-width: 0;
}

.documents-legal-file-card__item-name {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #f8fafc;
}

.documents-legal-file-card__item-dates {
  margin: 2px 0 0;
  font-size: 11px;
  color: #cbd5e1;
}

.documents-legal-file-card__assign-btn {
  margin: 4px 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f8fafc;
  font-size: 11px;
  font-weight: 700;
  text-decoration: none;
}

.documents-legal-file-card__assign-btn:hover,
.documents-legal-file-card__assign-btn:focus {
  color: #ffffff;
}

.documents-legal-file-card__item-comments {
  margin: 2px 0 0;
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.documents-legal-file-card__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.documents-legal-file-card__report-btn {
  margin: 14px auto 0;
  width: fit-content;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.95) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
}

.documents-legal-file-card__report-btn:hover,
.documents-legal-file-card__report-btn:focus {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.45);
}
</style>
